/**
 * @file CandidatePopup.jsx
 * @author Rishikesh
 * @date 2024-10-08
 * @description Component used in details page to covert the lead to candidate
 */
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  Box,
  Typography,
  Stack,
  IconButton,
  Autocomplete,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { Close } from "@mui/icons-material";
import {
  emailRegex,
  numberRegex,
  phoneNumberRegex,
} from "../../../components/constants/regex";

export const CandidatePopup = ({
  open,
  onClose,
  onSubmit,
  lead,
  selectedCourse,
  setSelectedCourse,
  selectedCountry,
  setSelectedCountry,
  countryList,
  courseCountryList,
  title = "Convert to Candidate",
  description = "Please review and verify the details of your lead to ensure no changes or modifications are necessary.",
  btn1Text = "Cancel",
  btn2Text = "Convert",
  hideRegistrationFee = false,
}) => {
  const [countryError, setCountryError] = useState("");
  const [courseError, setCourseError] = useState("");
  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      candidateuuid: lead?.candidateuuid || "",
      name: lead?.name || "",
      email: lead?.email || "",
      personalphone: lead?.personalphone || "",
      registrationfee: "",
    },
  });

  React.useEffect(() => {
    if (open && lead && (lead.candidateuuid || lead.name || lead.email)) {
      setValue("candidateuuid", lead.candidateuuid || "");
      setValue("name", lead.name || "");
      setValue("email", lead.email || "");
      setValue("personalphone", lead.personalphone || "");
      if (!hideRegistrationFee) {
        setValue("registrationfee", "");
      }
      setSelectedCountry(
        lead.coursecountry?.[0]?.countryid
          ? {
              id: lead.coursecountry[0].countryid,
              name: lead.coursecountry[0].countryname,
            }
          : null
      );
      setSelectedCourse(
        lead.coursecountry?.[0]?.courses?.[0]
          ? {
              courseid: lead.coursecountry[0].courses[0],
              coursename: lead.coursecountry[0].coursename,
            }
          : null
      );
      setCountryError("");
      setCourseError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead, setValue]);

  const validatePersonalPhone = (value) => {
    if (value.slice(0, 6) === "XXXXXX") {
      return true;
    }
    const isValid = phoneNumberRegex.test(value);

    return isValid || "Enter a valid phone number";
  };

  const handleClose = () => {
    if (!hideRegistrationFee) {
      setValue("registrationfee", "");
      setSelectedCountry(null);
      setSelectedCourse(null);
    }
    onClose();
  };

  const handleFormSubmit = (data) => {
    setCountryError("");
    setCourseError("");

    if (!selectedCountry || Object.keys(selectedCountry).length === 0) {
      setCountryError("Country is required");
    }
    if (!selectedCourse || Object.keys(selectedCourse).length === 0) {
      setCourseError("Course is required");
    }

    if (
      selectedCountry &&
      Object.keys(selectedCountry).length > 0 &&
      selectedCourse &&
      Object.keys(selectedCourse).length > 0
    ) {
      onSubmit(data);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Stack alignSelf="end" p={1}>
        <IconButton sx={{ color: "black" }} onClick={handleClose}>
          <Close />
        </IconButton>
      </Stack>
      <DialogTitle>
        <Typography
          variant="h4"
          align="center"
          color="primary"
          sx={{ fontWeight: 600 }}
        >
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography
          variant="body2"
          align="center"
          gutterBottom
          sx={{ color: "#434343", mb: 2 }}
        >
          {description}
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit(handleFormSubmit)}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Name</Typography>
            <TextField
              placeholder="Enter full name"
              className="inputMdRounded"
              sx={{ bgcolor: "transparent", width: "70%" }}
              error={!!errors.name}
              helperText={errors.name ? errors.name.message : ""}
              {...register("name", { required: "Name is required" })}
            />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Email</Typography>
            <TextField
              placeholder="Enter email address"
              className="inputMdRounded"
              sx={{ bgcolor: "transparent", width: "70%" }}
              error={!!errors.email}
              helperText={errors.email ? errors.email.message : ""}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: emailRegex,
                  message: "Enter a valid email",
                },
              })}
            />
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Personal No.</Typography>
            <TextField
              placeholder="Enter personal number"
              className="inputMdRounded"
              sx={{ bgcolor: "transparent", width: "70%" }}
              error={!!errors.personalphone}
              slotProps={{ htmlInput: { maxLength: 12 } }}
              helperText={
                errors.personalphone ? errors.personalphone.message : ""
              }
              {...register("personalphone", {
                required: "Personal No. is required",
                validate: validatePersonalPhone,
              })}
            />
          </Stack>

          {!hideRegistrationFee && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={"space-between"}
            >
              <Typography>Reg. Fee</Typography>
              <TextField
                placeholder="Enter Reg. Fee"
                className="inputMdRounded"
                sx={{ bgcolor: "transparent", width: "70%" }}
                error={!!errors.registrationfee}
                helperText={
                  errors.registrationfee ? errors.registrationfee.message : ""
                }
                {...register("registrationfee", {
                  required: "Reg. Fee is required",
                  pattern: {
                    value: numberRegex,
                    message: "Enter numeric value",
                  },
                })}
              />
            </Stack>
          )}
          <Box display="flex" justifyContent="space-between" gap={2}>
            <FormControl fullWidth>
              <Autocomplete
                sx={{
                  "& .MuiInputBase-input": {
                    m: 0,
                    p: "4px 8px",
                    minWidth: 120,
                  },
                  "& .MuiOutlinedInput-root": {
                    mt: 0,
                    borderRadius: "12px",
                    height: 42,
                  },
                  width: "auto",
                }}
                options={countryList}
                getOptionLabel={(option) => option.country_name}
                value={selectedCountry && selectedCountry.id 
                  ? countryList.find((c) => c.id === selectedCountry.id) || null
                  : null}
                onChange={(event, newValue) => {
                  setSelectedCountry(newValue || null);
                  setSelectedCourse(null);
                  setCountryError("");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    error={!!countryError}
                    helperText={countryError}
                  />
                )}
                isOptionEqualToValue={(option, value) => 
                  value ? option.id === value.id : false
                }
                noOptionsText="No country found"
              />
            </FormControl>

            <FormControl fullWidth>
              <Autocomplete
                sx={{
                  "& .MuiInputBase-input": {
                    m: 0,
                    p: "4px 8px",
                    minWidth: 120,
                  },
                  "& .MuiOutlinedInput-root": {
                    mt: 0,
                    borderRadius: "12px",
                    height: 42,
                  },
                  width: "auto",
                }}
                options={courseCountryList}
                getOptionLabel={(option) => option.coursename}
                value={selectedCourse && selectedCourse.courseid
                  ? courseCountryList.find(
                      (c) => c?.courseid === selectedCourse.courseid
                    ) || null
                  : null}
                onChange={(event, newValue) => {
                  setSelectedCourse(newValue || null);
                  setCourseError("");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    error={!!courseError}
                    helperText={courseError}
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  value ? option.courseid === value.courseid : false
                }
                noOptionsText="No courses found"
              />
            </FormControl>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          alignSelf: "end",
          gap: 1,
          mt: 2,
          mb: 1,
        }}
      >
        <Button onClick={handleClose} variant="outlined" color="error">
          {btn1Text}
        </Button>
        <Button
          onClick={handleSubmit(handleFormSubmit)}
          variant="contained"
          color="success"
        >
          {btn2Text}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
